import { FileSearchOutlined } from '@ant-design/icons';
import { useGetBackProcedimentiIdFile } from '@framework/api-client/api/backoffice/procedimenti/procedimenti.ts';
import { type ElencoFileDocumento } from '@framework/api-client/api/inquiries-compile/schemas/elencoFileDocumento.ts';
import { type TipoComunicazioneFileAllegatoDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoComunicazioneFileAllegatoDTO.ts';
import { useField } from '@framework/form/field/useField.tsx';
import { ModalDialog } from '@framework/ui/components/modal/ModalDialog.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { getNetworkErrorMessage } from '@framework/ui/core/errors/getNetworkErrorMessage.tsx';
import {
  Alert,
  Button,
  Input,
  message,
  Space,
  type TablePaginationConfig,
  type UploadFile,
} from 'antd';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useCommunicationsContext } from '../context/CommunicationsContext.tsx';
import { useSelectedCommunicationType } from './hooks/useSelectedCommunicationType.tsx';

const $Button = styled(Button)`
  /* border-left: 0; */
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: -1px;
  z-index: 0;
  &:hover {
    z-index: 2;
  }
`;

export interface IAttachmentFromProcedureDialogProps {
  disabled?: boolean;
  attachmentGroup: TipoComunicazioneFileAllegatoDTO;
  setFileList: Dispatch<SetStateAction<Array<UploadFile<any>>>>;
}

/**
 * Dialog per allegare un file da un procedimento
 */
export function AttachmentFromProcedureDialog(
  props: IAttachmentFromProcedureDialogProps,
) {
  const { disabled, attachmentGroup, setFileList } = props;

  const { t } = useTranslation();

  const selectedCommunicationType = useSelectedCommunicationType();

  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState<ElencoFileDocumento | undefined>(
    undefined,
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const { procedureId, attachExisting } = useCommunicationsContext();
  const {
    data,
    error,
    isFetching,
    isLoading: isListLoading,
  } = useGetBackProcedimentiIdFile(procedureId!);

  const { setValue: setAttachments } = useField('allegati');
  const attachmentsList = useWatch({ name: 'allegati' });

  const pagination: TablePaginationConfig = {
    total: data?.length ?? 0,
    pageSize: 10,
  };
  return (
    <>
      <$Button
        icon={<FileSearchOutlined />}
        disabled={disabled}
        type="default"
        size="large"
        onClick={() => {
          setModalOpen(true);
        }}
      >
        Allega da procedimento
      </$Button>
      <ModalDialog
        destroyOnClose
        title={`Allega "${attachmentGroup.denominazione}" dal procedimento`}
        open={modalOpen}
        onCancel={() => {
          setSelected(undefined);
          setModalOpen(false);
        }}
        footer={
          <FlexRow
            left={<h4>{selected?.nomeFile ?? undefined}</h4>}
            right={
              <Space>
                <Button
                  onClick={() => {
                    setSelected(undefined);
                    setModalOpen(false);
                  }}
                >
                  Annulla
                </Button>
                <Button
                  disabled={selected == undefined}
                  loading={isLoading}
                  type="primary"
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      if (!selectedCommunicationType?.id) {
                        // biome-ignore format: <tbd>
                        message.error(t('communication-procedure-attachment.error-no-communication-id', 'Errore: comunicazione non trovata'));
                        return;
                      }
                      if (!attachmentGroup.id) {
                        // biome-ignore format: <tbd>
                        message.error(t('communication-procedure-attachment.error-no-attachment-group-id', 'Errore: gruppo allegati non trovato'));
                        return;
                      }
                      if (!selected) {
                        // biome-ignore format: <tbd>
                        message.error(t('communication-procedure-attachment.error-no-file-selected', 'Errore: nessun file selezionato'));
                        return;
                      }
                      const result = await attachExisting?.(
                        selectedCommunicationType.id,
                        attachmentGroup.id,
                        selected,
                      );

                      if (!result) {
                        // biome-ignore format: <tbd>
                        message.error(t('communication-procedure-attachment.error-no-result','Errore: si è verificato un errore durante il caricamento del file'));
                        return;
                      }

                      setAttachments([
                        ...(attachmentsList ?? []),
                        { id: result.id },
                      ]);

                      setFileList((old: UploadFile[]) => [
                        ...old,
                        {
                          uid: result.id,
                          name: selected.nomeFile,
                          status: 'done',
                        } as UploadFile,
                      ]);

                      setModalOpen(false);
                      setSelected(undefined);
                    } catch (e) {
                      message.error(
                        getNetworkErrorMessage(
                          e,
                          `Si è verificato un errore durante l'aggiunta dell'allegato`,
                        ),
                      );
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  Allega
                </Button>
              </Space>
            }
          />
        }
      >
        <FlexRow style={{ maxWidth: 600, marginBottom: 16 }} gutter={2}>
          <Input
            size="middle"
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
            }}
            placeholder={'Cerca...'}
          />
        </FlexRow>
        {error && (
          <>
            <Alert
              type="error"
              showIcon
              message={getNetworkErrorMessage(
                error,
                t(
                  'communication-procedure-attachment.error-loading-files',
                  "Si è verificato un errore durante il caricamento dell'elenco dei file del procedimento",
                ),
              )}
            />
            <VerticalSpace />
          </>
        )}
        {/* Tabella con tutti i file tornati dall'API con vicino un radio button */}
        <TableData
          darkTitleBar={false}
          showSorterTooltip={false}
          size="small"
          loading={isListLoading || isFetching}
          dataSource={data?.filter(d => {
            if (searchQuery === '') return true;

            return d.nomeFile
              ?.toLowerCase()
              ?.includes(searchQuery.toLowerCase());
          })}
          rowSelection={{
            type: 'radio',
            onChange: (_, selectedRows) => {
              setSelected(selectedRows[0]);
            },
          }}
          pagination={pagination}
          columns={[
            {
              title: 'Nome file',
              dataIndex: 'nomeFile',
            },
            {
              title: 'Tipo',
              dataIndex: 'tipo',
            },
          ]}
        />
      </ModalDialog>
    </>
  );
}
